import { Chess } from 'chess.js'
import type { Square, PieceSymbol, Color } from 'chess.js'

export type { Square, PieceSymbol, Color }

export interface ChessPiece {
  type:  PieceSymbol
  color: Color
}

// Full 8x8 board: indexed by square
export type BoardMap = Partial<Record<Square, ChessPiece>>

export function parseFen(fen: string): BoardMap {
  try {
    const chess = new Chess(fen)
    const result: BoardMap = {}
    chess.board().forEach((row) =>
      row.forEach((sq) => {
        if (sq) result[sq.square] = { type: sq.type, color: sq.color }
      })
    )
    return result
  } catch {
    return {}
  }
}

export function getValidMoveSquares(fen: string, square: Square): Square[] {
  try {
    const chess = new Chess(fen)
    return chess.moves({ square, verbose: true }).map((m) => m.to as Square)
  } catch {
    return []
  }
}

// Returns new FEN after applying move, or null if illegal
export function applyMove(fen: string, from: Square, to: Square): string | null {
  try {
    const chess = new Chess(fen)
    const result = chess.move({ from, to, promotion: 'q' })
    return result ? chess.fen() : null
  } catch {
    return null
  }
}

export function isCheck(fen: string): boolean {
  try { return new Chess(fen).isCheck() } catch { return false }
}

export function isCheckmate(fen: string): boolean {
  try { return new Chess(fen).isCheckmate() } catch { return false }
}

export function isGameOver(fen: string): boolean {
  try { return new Chess(fen).isGameOver() } catch { return false }
}

export function sideToMove(fen: string): Color {
  return fen.split(' ')[1] as Color ?? 'w'
}

// Parse a UCI move string like "e2e4" into { from, to }
export function parseUci(uci: string): { from: Square; to: Square } | null {
  if (uci.length < 4) return null
  const from = uci.slice(0, 2) as Square
  const to   = uci.slice(2, 4) as Square
  return { from, to }
}

// ── Custom move calculator (no kings needed; used for BFS in Capture mode) ──

const FILES = 'abcdefgh'

function sqToCoord(sq: Square): [number, number] {
  return [FILES.indexOf(sq[0]), parseInt(sq[1]) - 1]
}

function coordToSq(file: number, rank: number): Square | null {
  if (file < 0 || file > 7 || rank < 0 || rank > 7) return null
  return `${FILES[file]}${rank + 1}` as Square
}

const KNIGHT_DELTAS = [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]] as const

// Returns squares a piece can reach from `from`, accounting for blockers.
// blockers = squares with pieces that block paths but can be captured.
export function getReachableSquares(
  piece: PieceSymbol,
  from: Square,
  blockers: Set<Square>,
): Square[] {
  const [ff, fr] = sqToCoord(from)
  const result: Square[] = []

  const slide = (df: number, dr: number) => {
    for (let i = 1; i <= 7; i++) {
      const sq = coordToSq(ff + df * i, fr + dr * i)
      if (!sq) break
      result.push(sq)
      if (blockers.has(sq)) break
    }
  }

  switch (piece) {
    case 'r': slide(1,0); slide(-1,0); slide(0,1); slide(0,-1); break
    case 'b': slide(1,1); slide(1,-1); slide(-1,1); slide(-1,-1); break
    case 'q':
      slide(1,0); slide(-1,0); slide(0,1); slide(0,-1)
      slide(1,1); slide(1,-1); slide(-1,1); slide(-1,-1)
      break
    case 'n':
      for (const [df, dr] of KNIGHT_DELTAS) {
        const sq = coordToSq(ff + df, fr + dr)
        if (sq) result.push(sq)
      }
      break
    case 'k':
      for (const df of [-1, 0, 1]) for (const dr of [-1, 0, 1]) {
        if (!df && !dr) continue
        const sq = coordToSq(ff + df, fr + dr)
        if (sq) result.push(sq)
      }
      break
    default: break
  }

  return result
}

// BFS: minimum moves for `piece` to capture all pawns from startSquare.
// Uses custom move calculator (no chess.js) — no king needed.
export function computeOptimalCaptureMoves(
  piece: PieceSymbol,
  startSquare: Square,
  pawnSquares: Square[],
): number {
  const n = pawnSquares.length
  if (n === 0) return 0
  const fullMask = (1 << n) - 1
  const pawnIdx  = new Map(pawnSquares.map((sq, i) => [sq, i]))

  type State = { sq: Square; mask: number }
  const keyOf = (s: State) => `${s.sq},${s.mask}`

  const queue: Array<State & { dist: number }> = [{ sq: startSquare, mask: 0, dist: 0 }]
  const visited = new Set<string>([keyOf({ sq: startSquare, mask: 0 })])

  while (queue.length > 0) {
    const { sq, mask, dist } = queue.shift()!
    if (mask === fullMask) return dist

    const remainingPawns = new Set(
      pawnSquares.filter((_, i) => !(mask & (1 << i)))
    )
    const moves = getReachableSquares(piece, sq, remainingPawns)

    for (const to of moves) {
      let newMask = mask
      const idx = pawnIdx.get(to)
      if (idx !== undefined) newMask |= (1 << idx)
      const next = { sq: to, mask: newMask }
      const key  = keyOf(next)
      if (!visited.has(key)) {
        visited.add(key)
        queue.push({ ...next, dist: dist + 1 })
      }
    }
  }

  return -1
}
